/**
 * Learning Center Header: the shared platform AppBar (h-12, the one gradient
 * in the system; design skill "App bar"). SiteSelector and the user menu live
 * in the `right` slot, the same pattern as every other module's Header.
 *
 * Hot file (plan 15.12): WP-03, then WP-48, which added the NotificationBell
 * (plan 9.4) to the right slot; nothing else edits it. The bell is the
 * summary poll's consumer (LmsProvider useLmsSummary), so the 60 s poll runs
 * while this header is mounted and stops when the module shell unmounts.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, User } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import SiteSelector from '../../../components/SiteSelector';
import AppBar from '../../../ui/AppBar';
import { NotificationBell } from '../NotificationBell';

export function Header() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <AppBar
      right={
        <>
          {/* plan 9.4 (WP-48): the module bell, before the site selector as in IXVerify's bar. */}
          <NotificationBell />
          <SiteSelector />

          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              aria-label="User menu"
              aria-expanded={userMenuOpen}
              className="flex min-h-[44px] items-center gap-1.5 rounded-lg p-1.5 text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 sm:min-h-0"
            >
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt={user.full_name || 'User'} className="h-7 w-7 rounded-full" />
              ) : (
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
                  <User size={16} aria-hidden="true" />
                </div>
              )}
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={`hidden transition-transform sm:block ${userMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.full_name || 'User'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                >
                  <LogOut size={16} aria-hidden="true" />
                  Back to Tool Hub
                </button>
              </div>
            )}
          </div>
        </>
      }
    />
  );
}

export default Header;
